/* Public client Order page — Sprint Client-Link-2.
 * Standalone: no ProtectedRoute, no MOShell, no workspace nav.
 * Uses a bare axios instance (no auth interceptor) so the platform token
 * from localStorage is never sent to the public endpoint.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { displayOrderNumber } from "@/lib/masterOrderApi";

const publicHttp = axios.create({ baseURL: API });

const getPublicOrder = (token) =>
  publicHttp.get(`/public/master-order/${encodeURIComponent(token)}`).then((r) => r.data);

/* ---------- formatting ------------------------------------------------ */
const NBSP = "\u00A0";
const RU_MONTHS_GEN = ["января","февраля","марта","апреля","мая","июня",
  "июля","августа","сентября","октября","ноября","декабря"];

const fmtDate = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate + (isoDate.length === 10 ? "T00:00:00" : ""));
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${RU_MONTHS_GEN[d.getMonth()]}`;
};
const fmtTime = (isoTime) => {
  if (!isoTime) return "";
  const t = String(isoTime).match(/(\d{2}):(\d{2})/);
  return t ? `${t[1]}:${t[2]}` : "";
};
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${RU_MONTHS_GEN[d.getMonth()]} · ${hh}:${mm}`;
};
const fmtWeight = (grams) => {
  if (grams == null || grams === "") return "";
  const n = Number(grams);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n >= 1000) {
    const kg = n / 1000;
    return `${String(kg.toFixed(3)).replace(/\.?0+$/, "").replace(".", ",")}${NBSP}кг`;
  }
  return `${Math.round(n)}${NBSP}г`;
};
const fmtMoneyMinor = (minor, currency) => {
  if (minor == null || currency == null) return "—";
  const abs = Math.abs(minor);
  const major = Math.floor(abs / 100);
  const cents = String(abs % 100).padStart(2, "0");
  const majorStr = String(major).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
  return `${majorStr},${cents}${NBSP}${currency}`;
};

const STATUS_LABEL = {
  in_progress: "Заказ готовится",
  ready: "Готов к выдаче",
  delivered: "Заказ выдан",
  cancelled: "Заказ отменён",
};
const DELIVERY_LABEL = {
  pickup: "Самовывоз",
  confectioner_delivery: "Доставка кондитером",
  courier: "Курьер",
  post_shipping: "Отправка",
  intercity: "Межгород",
  custom: "Особый способ",
};

/* ---------- head robots (idempotent) ---------------------------------- */
function useNoIndex() {
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    const meta = existing || document.createElement("meta");
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex, nofollow");
    if (!existing) document.head.appendChild(meta);
    return () => { if (!existing && meta.parentNode) meta.parentNode.removeChild(meta); };
  }, []);
}

/* ---------- layout primitives ----------------------------------------- */
const Section = ({ title, children, testid }) => (
  <section className="mb-8" data-testid={testid}>
    <div className="text-[11px] tracking-[0.18em] uppercase mb-3"
         style={{ color: "#8B6F4B" }}>{title}</div>
    {children}
  </section>
);
const Row = ({ label, value, testid }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="text-[13px]" style={{ color: "#5B4326" }}>{label}</span>
    <span className="text-[14px] tabular-nums whitespace-nowrap" style={{ color: "#2A1D14" }}
          data-testid={testid}>{value}</span>
  </div>
);

/* ---------- states ---------------------------------------------------- */
function InvalidLinkView() {
  useNoIndex();
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#FBF5EA" }}>
      <div className="max-w-md text-center" data-testid="public-order-invalid">
        <div className="text-[13px] tracking-[0.24em] uppercase mb-4" style={{ color: "#8B6F4B" }}>
          Anokina Studio
        </div>
        <div className="text-[26px] mb-3"
             style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          Ссылка недействительна
        </div>
        <div className="text-[14px] leading-relaxed" style={{ color: "#5B4326" }}>
          Эта ссылка больше не работает. Попросите кондитера отправить Вам новую.
        </div>
      </div>
    </div>
  );
}

function ErrorView({ onRetry }) {
  useNoIndex();
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#FBF5EA" }}>
      <div className="max-w-md text-center" data-testid="public-order-error">
        <div className="text-[13px] tracking-[0.24em] uppercase mb-4" style={{ color: "#8B6F4B" }}>
          Anokina Studio
        </div>
        <div className="text-[22px] mb-4"
             style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
          Не удалось загрузить заказ
        </div>
        <button onClick={onRetry}
                className="h-10 px-5 rounded-xl text-[13px]"
                style={{ background: "#4A3728", color: "#F7EFE0" }}
                data-testid="public-order-retry">
          Повторить
        </button>
      </div>
    </div>
  );
}

/* ---------- page ------------------------------------------------------ */
export default function PublicOrderView() {
  useNoIndex();
  const { token } = useParams();
  const [state, setState] = useState({ phase: "loading" });
  const [heroIdx, setHeroIdx] = useState(0);
  const backendUrl = useRef(process.env.REACT_APP_BACKEND_URL);

  const load = async () => {
    setState({ phase: "loading" });
    try {
      const dto = await getPublicOrder(token);
      setState({ phase: "ok", dto });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) setState({ phase: "invalid" });
      else setState({ phase: "error" });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  if (state.phase === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ background: "#FBF5EA", color: "#8B6F4B" }}>
        <div className="text-[13px] tracking-[0.18em]">Загружаем…</div>
      </div>
    );
  }
  if (state.phase === "invalid") return <InvalidLinkView />;
  if (state.phase === "error")   return <ErrorView onRetry={load} />;

  const dto = state.dto;
  const statusLabel = STATUS_LABEL[dto.business_status_public] || null;
  const eventStr = [fmtDate(dto.event_date), fmtTime(dto.event_time)].filter(Boolean).join(" · ");
  const photos = (dto.finished_photos || []).map((p) => ({
    ...p, absoluteUrl: `${backendUrl.current}${p.url}`,
  }));
  const heroPhoto = photos[heroIdx] || photos[0];
  const pay = dto.payment_summary || {};

  return (
    <div className="min-h-screen" style={{ background: "#FBF5EA" }}>
      {/* Header */}
      <header className="pt-8 pb-4 px-5 md:px-8" data-testid="public-order-header">
        <div className="max-w-2xl mx-auto flex flex-col items-center text-center">
          <div className="text-[12px] tracking-[0.26em] uppercase"
               style={{ color: "#8B6F4B" }}>Anokina Studio</div>
          {dto.confectioner_display_name && (
            <div className="text-[13px] mt-1" style={{ color: "#5B4326" }}
                 data-testid="public-order-confectioner">
              {dto.confectioner_display_name}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 md:px-8 pb-16">
        {/* Hero */}
        <div className="text-center mb-8">
          {dto.client_display_name && (
            <div className="text-[13px] mb-1" style={{ color: "#8B6F4B" }}
                 data-testid="public-order-greeting">
              Для {dto.client_display_name}
            </div>
          )}
          <h1 className="text-[34px] md:text-[42px] leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
              data-testid="public-order-number">
            Заказ №&nbsp;{displayOrderNumber(dto.order_number)}
          </h1>
          {eventStr && (
            <div className="text-[15px] mt-2" style={{ color: "#4A3728" }}
                 data-testid="public-order-event">
              {eventStr}{dto.event_type ? ` · ${dto.event_type}` : ""}
            </div>
          )}
          {statusLabel && (
            <div className="mt-4 inline-block px-4 py-1.5 rounded-full text-[12.5px]"
                 style={{ background: "#EADFCF", color: "#4A3728" }}
                 data-testid="public-order-status">
              {statusLabel}
            </div>
          )}
          {dto.business_status_public === "delivered" && dto.handed_over_at && (
            <div className="mt-2 text-[13px]" style={{ color: "#5B4326" }}
                 data-testid="public-order-handed-over">
              Выдан {fmtDateTime(dto.handed_over_at)}
            </div>
          )}
          {dto.recipient_display_name && (
            <div className="mt-3 text-[13px]" style={{ color: "#5B4326" }}
                 data-testid="public-order-recipient">
              Получатель: {dto.recipient_display_name}
            </div>
          )}
        </div>

        {/* Finished photos */}
        {photos.length > 0 && (
          <Section title="Готовый заказ" testid="public-order-photos">
            <div className="rounded-2xl overflow-hidden bg-white"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0",
                          background: "#FBF5EA" }}>
              <img src={heroPhoto.absoluteUrl}
                   alt="Готовый заказ"
                   className="block w-full h-auto object-contain"
                   style={{ maxHeight: "80vh" }}
                   data-testid="public-order-hero-photo" />
            </div>
            {photos.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1"
                   data-testid="public-order-photo-strip">
                {photos.map((p, i) => (
                  <button key={p.url} onClick={() => setHeroIdx(i)}
                          className="shrink-0 rounded-lg overflow-hidden"
                          style={{
                            width: 72, height: 72,
                            boxShadow: i === heroIdx
                              ? "inset 0 0 0 2px #4A3728" : "inset 0 0 0 1px #EFE4D0",
                          }}>
                    <img src={p.absoluteUrl} alt=""
                         className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* Items */}
        {dto.items && dto.items.length > 0 && (
          <Section title="Ваш заказ" testid="public-order-items">
            <div className="space-y-2">
              {dto.items.map((it, idx) => (
                <div key={idx} className="rounded-xl bg-white p-4"
                     style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                     data-testid="public-order-item">
                  <div className="text-[15px]" style={{ color: "#2A1D14" }}>
                    {it.product_type_name || "Позиция"}
                    {it.quantity > 1 ? (
                      <span className="text-[12px] ml-2" style={{ color: "#8B6F4B" }}>
                        × {it.quantity}
                      </span>
                    ) : null}
                  </div>
                  {(it.flavor_composition || it.size_display) && (
                    <div className="text-[12.5px] mt-1" style={{ color: "#5B4326" }}>
                      {[it.flavor_composition, it.size_display].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {it.actual_weight_grams != null && (
                    <div className="text-[12px] mt-1" style={{ color: "#8B6F4B" }}
                         data-testid="public-order-item-weight">
                      Фактический вес: {fmtWeight(it.actual_weight_grams)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Payment */}
        {pay && pay.currency_code && (
          <Section title="Оплата" testid="public-order-payment">
            <div className="rounded-xl bg-white p-4"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
              {pay.is_total_reliable ? (
                <>
                  <Row label="Стоимость заказа"
                       value={fmtMoneyMinor(pay.sale_total_minor, pay.currency_code)}
                       testid="public-order-pay-total" />
                  <Row label="Оплачено"
                       value={fmtMoneyMinor(pay.net_paid_minor, pay.currency_code)}
                       testid="public-order-pay-paid" />
                  {pay.remaining_minor === 0 && (pay.overpayment_minor || 0) === 0 ? (
                    <div className="pt-2 text-[13px]" style={{ color: "#4A3728" }}
                         data-testid="public-order-pay-fully-paid">Оплачено полностью</div>
                  ) : (pay.overpayment_minor || 0) > 0 ? (
                    <Row label="Переплата"
                         value={fmtMoneyMinor(pay.overpayment_minor, pay.currency_code)}
                         testid="public-order-pay-overpayment" />
                  ) : (
                    <Row label={`К${NBSP}доплате`}
                         value={fmtMoneyMinor(pay.remaining_minor, pay.currency_code)}
                         testid="public-order-pay-remaining" />
                  )}
                </>
              ) : (
                <>
                  <Row label="Оплачено"
                       value={fmtMoneyMinor(pay.net_paid_minor, pay.currency_code)}
                       testid="public-order-pay-paid" />
                  <div className="pt-2 text-[13px] leading-relaxed"
                       style={{ color: "#5B4326" }}
                       data-testid="public-order-pay-partial-total">
                    <div>Итоговая стоимость ещё рассчитывается</div>
                    <div className="text-[12px]" style={{ color: "#8B6F4B" }}>
                      Не для всех позиций заказа готов расчёт.
                    </div>
                  </div>
                </>
              )}
            </div>
          </Section>
        )}

        {/* Delivery */}
        {dto.delivery && (
          <Section title="Как получить заказ" testid="public-order-delivery">
            <div className="rounded-xl bg-white p-4 space-y-1.5 text-[13.5px]"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}>
              <div style={{ color: "#4A3728" }}>
                {DELIVERY_LABEL[dto.delivery.type] || "Получение"}
              </div>
              {(dto.delivery.date || dto.delivery.time_window) && (
                <div style={{ color: "#5B4326" }}>
                  {[fmtDate(dto.delivery.date), dto.delivery.time_window]
                    .filter(Boolean).join(" · ")}
                </div>
              )}
              {dto.delivery.address && (
                <div className="break-words" style={{ color: "#5B4326" }}>
                  {dto.delivery.address}
                </div>
              )}
              {dto.delivery.service_name && (
                <div style={{ color: "#8B6F4B" }}>{dto.delivery.service_name}</div>
              )}
              {dto.delivery.tracking_number && (
                <div className="break-all text-[12.5px]" style={{ color: "#8B6F4B" }}>
                  Трек-номер: {dto.delivery.tracking_number}
                </div>
              )}
            </div>
          </Section>
        )}

        {/* Handoff message */}
        {dto.handoff_message && (
          <Section title="Сообщение от кондитера" testid="public-order-message">
            <div className="rounded-xl bg-white p-4 text-[14px] leading-relaxed whitespace-pre-line"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}>
              {dto.handoff_message}
            </div>
          </Section>
        )}

        {/* Storage instructions */}
        {dto.storage_instructions && (
          <Section title="Хранение и перевозка" testid="public-order-storage">
            <div className="rounded-xl bg-white p-4 text-[13.5px] leading-relaxed whitespace-pre-line"
                 style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", color: "#2A1D14" }}>
              {dto.storage_instructions}
            </div>
          </Section>
        )}

        <div className="text-center text-[11px] mt-4" style={{ color: "#B9A692" }}>
          Anokina Studio
        </div>
      </main>
    </div>
  );
}
